import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';
import { OrdersTable } from '@/features/orders/OrdersTable';
import { CalendarPopover } from '@/routes/restaurant/orders/CalendarPopover';
import { LivePhoneCallsCard } from './LivePhoneCallsCard';
import { OrderGenerator } from './OrderGenerator';

const LIMIT = 20;

export const Route = createFileRoute('/r/$restaurantId/orders/')({
  component: OrderListPage,
});

function OrderListPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [limitResultSet, setLimitResultSet] = useState<number | undefined>(LIMIT);

  const { restaurantId } = Route.useParams();
  // The picked calendar day is the restaurant's businessDate; the server merges `orders`
  // (current session) with the archive (settled at register closure).
  const ordersQuery = useQuery(api.orders.listByRestaurantId, {
    restaurantId,
    businessDate: format(selectedDate, 'yyyy-MM-dd'),
    limit: limitResultSet,
  });

  useEffect(() => {
    setLimitResultSet(LIMIT);
  }, [selectedDate]);

  if (ordersQuery.isError) {
    return <ErrorAlert message={ordersQuery.error.message} title="Error loading orders" />;
  }

  return (
    <>
      <div className="mb-2 flex justify-between">
        <SuperAdminOnly>
          <OrderGenerator />
        </SuperAdminOnly>
        <CalendarPopover
          date={selectedDate}
          setDate={(date) => setSelectedDate(date ?? new Date())}
        />
      </div>
      <LivePhoneCallsCard />
      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {ordersQuery.isPending ? (
            <LoadingIcon />
          ) : (
            <OrdersTable orders={ordersQuery.data} showRestaurantColumn={false} />
          )}
          {ordersQuery.data && limitResultSet === ordersQuery.data.length && (
            <div className={'flex justify-center mt-3'}>
              <ActionButton variant={'link'} onClick={() => setLimitResultSet(undefined)}>
                Show more...
              </ActionButton>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
